import {
  resolveAppToastDuration,
  resolveAppToastPosition,
} from "./resolveAppToastDuration";

export const APP_TOAST_TRACE = "[APP_TOAST_TRACE]";

export const APP_TOAST_MAX_VISIBLE = 3;

/** @typedef {"success"|"error"|"warning"|"info"} AppToastType */

/** @type {Set<(event: { action: string, payload: object }) => void>} */
const subscribers = new Set();

/**
 * @param {(event: { action: string, payload: object }) => void} listener
 */
export function subscribeAppToasts(listener) {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

function emit(action, payload) {
  subscribers.forEach((listener) => {
    try {
      listener({ action, payload });
    } catch {
      /* ignore subscriber errors */
    }
  });
}

/**
 * @param {object} input
 * @param {AppToastType} [input.type]
 * @param {string} [input.title]
 * @param {string} [input.message]
 * @param {number} [input.duration]
 * @param {string} [input.source]
 */
export function showAppToast(input = {}) {
  const type = ["success", "error", "warning", "info"].includes(input.type)
    ? input.type
    : "info";
  const title = input.title != null ? String(input.title).trim() : "";
  const message = input.message != null ? String(input.message).trim() : "";
  const source = input.source != null ? String(input.source).trim() : "unknown";
  const duration = resolveAppToastDuration(type, input.duration);
  const position = resolveAppToastPosition();

  const toast = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    title,
    message,
    source,
    duration,
    position,
    createdAt: Date.now(),
  };

  logAppToastTrace({
    type: toast.type,
    title: toast.title,
    message: toast.message,
    source: toast.source,
    duration: toast.duration,
    position: toast.position,
    actionTaken: "show",
  });

  emit("add", toast);
  return toast.id;
}

/**
 * @param {string} id
 * @param {"manual_close"|"auto_dismiss"|"replaced"} [actionTaken]
 */
export function dismissAppToast(id, actionTaken = "manual_close") {
  if (!id) return;
  logAppToastTrace({
    type: "dismiss",
    title: "",
    message: "",
    source: "",
    duration: 0,
    position: resolveAppToastPosition(),
    actionTaken,
  });
  emit("remove", { id, actionTaken });
}

/** @param {object} payload */
export function logAppToastTrace(payload) {
  console.info(APP_TOAST_TRACE, {
    type: payload.type ?? "info",
    title: payload.title ?? "",
    message: payload.message ?? "",
    source: payload.source ?? "unknown",
    duration: payload.duration ?? 0,
    position: payload.position ?? resolveAppToastPosition(),
    actionTaken: payload.actionTaken ?? "show",
  });
}

export function showAppToastSuccess(message, options = {}) {
  return showAppToast({ ...options, type: "success", message });
}

export function showAppToastError(message, options = {}) {
  return showAppToast({ ...options, type: "error", message });
}

export function showAppToastWarning(message, options = {}) {
  return showAppToast({ ...options, type: "warning", message });
}

export function showAppToastInfo(message, options = {}) {
  return showAppToast({ ...options, type: "info", message });
}
